import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

type ClientOption = { supabase?: SupabaseClient };

export type RecordSearchEvent = { id: string; name: string | null; code: string | null; status: string | null; start: string | null };
export type RecordSearchPlace = { id: string; name: string; city: string | null; state: string | null };
export type RecordSearchResults = { events: RecordSearchEvent[]; customers: RecordSearchPlace[]; venues: RecordSearchPlace[] };

/** The shortest term the server searches: its trigram indexes need three characters. */
export const RECORD_SEARCH_MIN_LENGTH = 3;

const EMPTY: RecordSearchResults = { events: [], customers: [], venues: [] };

/**
 * Events, customers and venues whose name or code matches (docs/record-search.md). Row-level security
 * decides what comes back. Pass the palette's signal so a newer term cancels this request.
 */
export async function searchRecords(query: string, signal?: AbortSignal, options?: ClientOption & { limit?: number }): Promise<RecordSearchResults> {
  const term = query.trim();
  if (term.length < RECORD_SEARCH_MIN_LENGTH) return EMPTY;
  const supabase = options?.supabase ?? getSupabaseClient();
  let request = supabase.rpc('search_records', { p_query: term, p_limit: options?.limit ?? 5 });
  if (signal) request = request.abortSignal(signal);
  const { data, error } = await request;
  if (error) throw error;
  const result = (data ?? {}) as Partial<RecordSearchResults>;
  return { events: result.events ?? [], customers: result.customers ?? [], venues: result.venues ?? [] };
}
